/**
 * Производство: загрузка таблиц (номенклатура, остатки, план, факт), этапы доски, смены,
 * журнал учёта времени (события и простои) и выработка по часам.
 */
import { z } from 'zod';

/* ---------- Загрузка таблиц ---------- */

export type ImportKind = 'catalog' | 'stock' | 'plan' | 'fact';
export const IMPORT_KIND_LABEL: Record<ImportKind, string> = {
  catalog: 'Номенклатура',
  stock: 'Остатки на складе',
  plan: 'План производства',
  fact: 'Факт производства',
};
export const IMPORT_KIND_HINT: Record<ImportKind, string> = {
  catalog: 'Наименование, артикул (SKU) и характеристики — каждая в своём столбце.',
  stock: 'Артикул или наименование, место хранения, количество. Остатки загруженных позиций заменяются.',
  plan: 'Артикул или наименование и количество (можно месяц). Карточки попадают в «План месяца».',
  fact: 'Дата, ФИО, артикул или наименование, количество — история выработки.',
};

/** Роли столбцов для каждого вида таблицы. attr — характеристика (только номенклатура). */
export type ColumnRole = 'skip' | 'sku' | 'name' | 'attr' | 'place' | 'qty' | 'date' | 'person' | 'period';
export const ROLE_LABEL_COL: Record<ColumnRole, string> = {
  skip: 'не загружать',
  sku: 'Артикул (SKU)',
  name: 'Наименование',
  attr: 'Характеристика',
  place: 'Место хранения',
  qty: 'Количество',
  date: 'Дата',
  person: 'ФИО',
  period: 'Месяц плана',
};
export const KIND_ROLES: Record<ImportKind, ColumnRole[]> = {
  catalog: ['skip', 'sku', 'name', 'attr'],
  stock: ['skip', 'sku', 'name', 'place', 'qty'],
  plan: ['skip', 'sku', 'name', 'qty', 'period'],
  fact: ['skip', 'sku', 'name', 'qty', 'date', 'person'],
};

export interface ImportPreviewDto {
  kind: ImportKind;
  /** Номер строки заголовков (с 0) и сами заголовки. */
  headerRow: number;
  headers: string[];
  /** Первые строки данных — показать, что получится. */
  sample: string[][];
  rows: number;
  /** Предложенные роли столбцов (из прошлой загрузки этого вида или по названиям). */
  roles: ColumnRole[];
  /** Временный ключ загруженного файла — для подтверждения. */
  token: string;
}

export const ImportCommit = z.object({
  token: z.string().regex(/^[a-f0-9-]{36}$/),
  headerRow: z.number().int().min(0).max(100),
  roles: z.array(z.enum(['skip', 'sku', 'name', 'attr', 'place', 'qty', 'date', 'person', 'period'])).max(300),
  /** План: месяц по умолчанию (ГГГГ-ММ), если в файле нет столбца месяца. */
  period: z.string().regex(/^\d{4}-\d{2}$/).optional(),
});
export type ImportCommit = z.input<typeof ImportCommit>;

export interface ImportResultDto {
  kind: ImportKind;
  rows: number;
  created: number;
  updated: number;
  skipped: number;
  /** Строки, которые не нашли номенклатуру (артикул/наименование) — первые. */
  unmatched: string[];
}

/** Какие характеристики номенклатуры показывать на карточке и в фильтрах. */
export interface AttrSetting {
  key: string;
  show: boolean;
}

/* ---------- Этапы доски производства ---------- */

export type Stage = 'plan_month' | 'plan_ops' | 'changeover' | 'production' | 'packing' | 'shipping';
export const STAGES: readonly Stage[] = ['plan_month', 'plan_ops', 'changeover', 'production', 'packing', 'shipping'];
export const STAGE_LABEL: Record<Stage, string> = {
  plan_month: 'План месяца',
  plan_ops: 'Оперативный план',
  changeover: 'Переналадка',
  production: 'Производство',
  packing: 'Упаковка',
  shipping: 'Отгрузка',
};

/** Карточка производства на доске. */
export interface ProdCardDto {
  id: string;
  stage: Stage;
  status: 'new' | 'assigned' | 'in_progress' | 'done';
  title: string;
  productId: string | null;
  sku: string | null;
  name: string | null;
  /** Характеристики номенклатуры — для фильтров и группировки. */
  attrs: Record<string, string>;
  qty: number | null;
  /** Сделано (сумма выработки). */
  done: number;
  period: string | null;
  areaId: string | null;
  equipmentId: string | null;
  equipmentName: string | null;
  shiftId: string | null;
  shiftLabel: string | null;
  assignees: { id: string; name: string }[];
  /** Заполнена ли переналадка у номенклатуры. */
  hasChangeover: boolean;
  canEdit: boolean;
}

export const CardMove = z.object({ stage: z.enum(['plan_month', 'plan_ops', 'changeover', 'production', 'packing', 'shipping']) });

/* ---------- Смены ---------- */

export interface ShiftDto {
  id: string;
  day: string;
  startAt: string;
  endAt: string;
  areaId: string | null;
  status: 'open' | 'closed';
  members: { id: string; name: string }[];
  closedAt: string | null;
  note: string | null;
}

/** Назначить карточку на смену: дата, время (по умолчанию 09:00–18:00), люди, машина. */
export const AssignShift = z.object({
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  from: z.string().regex(/^\d{2}:\d{2}$/).default('09:00'),
  to: z.string().regex(/^\d{2}:\d{2}$/).default('18:00'),
  assigneeIds: z.array(z.string().uuid()).min(1, 'Кому?').max(30),
  equipmentId: z.string().uuid().nullable().optional(),
  areaId: z.string().uuid().nullable().optional(),
  qty: z.number().min(0).max(1e9).nullable().optional(),
});
export type AssignShift = z.input<typeof AssignShift>;

/* ---------- Журнал времени ---------- */

export type EventKind = 'changeover' | 'production' | 'packing' | 'check' | 'downtime' | 'other';
export const EVENT_KIND_LABEL: Record<EventKind, string> = {
  changeover: 'Переналадка',
  production: 'Производство',
  packing: 'Упаковка',
  check: 'Проверка оборудования',
  downtime: 'Простой',
  other: 'Другое',
};
export const DEFAULT_DOWNTIME = ['Ожидание материала', 'Поломка оборудования', 'Настройка / подналадка', 'Нет задания', 'Перерыв', 'Уборка', 'Прочее'];

export interface TimeEventDto {
  id: string;
  employeeId: string;
  employeeName: string;
  shiftId: string | null;
  taskId: string | null;
  taskTitle: string | null;
  equipmentId: string | null;
  equipmentName: string | null;
  kind: EventKind;
  reason: string | null;
  title: string | null;
  startedAt: string;
  endedAt: string | null;
  comment: string | null;
  files: { id: string; kind: 'photo' | 'video' | 'doc'; title: string; size: number; url: string; step: boolean; mime: string; createdAt: string }[];
}

export const EventStart = z.object({
  kind: z.enum(['changeover', 'production', 'packing', 'check', 'other']),
  taskId: z.string().uuid().nullable().optional(),
  equipmentId: z.string().uuid().nullable().optional(),
  title: z.string().trim().max(200).nullable().optional(),
});
export const EventFinish = z.object({
  comment: z.string().trim().max(3000).nullable().optional(),
  fileIds: z.array(z.string().uuid()).max(10).optional(),
});
/** Отметить простой: время без события (с конца прошлого события до сейчас) или с указанного времени. */
export const DowntimeInput = z.object({
  reason: z.string().trim().min(1, 'Тип простоя?').max(120),
  comment: z.string().trim().max(3000).nullable().optional(),
  from: z.string().datetime({ offset: true }).optional(),
  fileIds: z.array(z.string().uuid()).max(10).optional(),
  equipmentId: z.string().uuid().nullable().optional(),
});

/* ---------- Выработка ---------- */

export const OutputInput = z.object({
  taskId: z.string().uuid(),
  qty: z.number().min(0).max(1e9),
  /** hour — сделано за час; total — итог с начала смены (счётчик). */
  mode: z.enum(['hour', 'total']).default('hour'),
});

/** Моя смена: панель оператора. */
export interface MyShiftDto {
  shift: ShiftDto | null;
  tasks: (ProdCardDto & { doneShift: number })[];
  current: TimeEventDto | null;
  /** С какого момента время без события (конец прошлого события или начало смены). */
  idleSince: string | null;
  events: TimeEventDto[];
  downtimeReasons: string[];
}

/** Производство по часам (онлайн): выработка, события, простои. */
export interface HourlyDto {
  day: string;
  hours: { hour: string; qty: number }[];
  total: number;
  plan: number;
  tasks: { id: string; title: string; qty: number | null; done: number }[];
  events: TimeEventDto[];
  downtimeMinutes: number;
}
